/**
 * Tiptap Document Schema
 *
 * Permissive Zod schema for a ProseMirror / Tiptap document tree, used as the
 * canonical structured representation of rich-text memory bodies. Stored on
 * `BlockTextContent.doc` (see `block.ts`) alongside the derived plaintext
 * `text` field — `text` is what feeds analyze/embed/full-text search; `doc`
 * is what the viewer renders.
 *
 * The shape is intentionally loose on `attrs` so individual node types can
 * carry whatever ProseMirror schema they need (heading levels, list types,
 * link hrefs, future mention/embed payloads) without us having to enumerate
 * every variant up front. Hard caps on depth, total node count, and text
 * length defend against pathological inputs (per `security-limits.mdc`).
 *
 * The plaintext extractor (`@api/lib/tiptap-doc.ts` server-side; the editor's
 * native `getText()` client-side) walks this same tree to derive the indexed
 * string — adding new node types is "free" as far as this schema is concerned;
 * the extractor just learns to handle the new types.
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
/**
 * Maximum nested depth of the document tree. A doc deeper than this is
 * almost certainly pathological — no legitimate user-authored memory needs
 * 13+ levels of nesting (a deeply-nested list with code blocks tops out
 * around 6–8). Caught at validation time so the server never tries to walk
 * the tree.
 */
const MAX_DOC_DEPTH = 12;
/**
 * Maximum total number of nodes (block + inline) in the document. 5,000
 * is generous — a 100-paragraph memory with rich inline marks fits well
 * under this. The cap exists to bound the cost of the plaintext walker
 * and any future doc transforms.
 */
const MAX_DOC_NODES = 5_000;
/**
 * Maximum length of a single text node's `text` payload. Block-level text
 * length is enforced separately on the derived plaintext (`block.content.text`,
 * 500K chars) — this is just a per-node ceiling so one giant text node
 * can't blow the JSON parser before the count/depth validators get to run.
 */
const MAX_TEXT_NODE_LENGTH = 50_000;
/**
 * Maximum length of a node `type` string. ProseMirror node names are
 * short identifiers (`paragraph`, `heading`, `bulletList`, etc.); 64
 * leaves room for any reasonable extension namespace.
 */
const MAX_TYPE_LENGTH = 64;
// ---------------------------------------------------------------------------
// Link href safety
// ---------------------------------------------------------------------------
/**
 * URI schemes the viewer is willing to emit as a live `<a>` and the schema
 * is willing to accept on a `link` mark's `href`. Mirrors the omnibar
 * editor's `Link.configure({ protocols: [...] })` allowlist so authoring,
 * persistence, and rendering all enforce the same set.
 *
 * Explicitly excludes `javascript:`, `data:`, `vbscript:`, and other
 * client-side script-execution schemes — a `link` mark with one of those
 * hrefs would otherwise fire arbitrary code on click and become an XSS
 * vector on every memory card the doc was rendered into.
 */
export const SAFE_LINK_SCHEMES = ["http", "https", "mailto"];
/**
 * Anchored at the start of the string with no whitespace tolerance — a
 * value like `\tjavascript:alert(1)` is rejected here, even though some
 * browsers strip leading control chars before resolving the scheme. The
 * `i` flag handles case-mutation bypass attempts (`JaVaScRiPt:`).
 */
const SAFE_LINK_SCHEME_RE = /^(?:https?|mailto):/i;
/**
 * True when `href` uses a render-safe scheme. Used by both the renderer
 * (drops unsafe link marks at paint time) and `TiptapMarkSchema` (rejects
 * unsafe link marks at validation time) so a malicious doc can't reach
 * persistence in the first place AND can't render a live `<a>` even if a
 * legacy doc smuggled one through before the schema check shipped.
 */
export function isSafeLinkHref(href) {
    return SAFE_LINK_SCHEME_RE.test(href);
}
// ---------------------------------------------------------------------------
// Recursive node schema
// ---------------------------------------------------------------------------
/**
 * A single mark applied to a text node (bold, italic, link, code, etc.).
 * Marks carry their own optional attrs (e.g. `link.attrs.href`).
 *
 * The shape is intentionally permissive on `attrs` (any keys, any values)
 * so individual mark types can carry whatever the underlying ProseMirror
 * extension needs — but `link` specifically is refined to reject hrefs
 * with unsafe URI schemes. Any other mark type's attrs pass through
 * unchanged.
 */
export const TiptapMarkSchema = z
    .object({
    type: z.string().min(1).max(MAX_TYPE_LENGTH),
    attrs: z.record(z.string(), z.unknown()).optional(),
})
    .superRefine((mark, ctx) => {
    if (mark.type !== "link")
        return;
    const href = mark.attrs?.href;
    // Non-string hrefs (missing, number, object) are dropped at render
    // time by `tiptap-render.tsx`'s `typeof === "string"` check, so they
    // can't reach a live `<a>`. Allow them through validation rather
    // than over-rejecting on shape.
    if (typeof href !== "string")
        return;
    if (isSafeLinkHref(href) === false) {
        ctx.addIssue({
            code: "custom",
            message: `Link mark href uses an unsupported scheme; only ${SAFE_LINK_SCHEMES.join(", ")} are allowed`,
            path: ["attrs", "href"],
        });
    }
});
export const TiptapNodeSchema = z.lazy(() => z.object({
    type: z.string().min(1).max(MAX_TYPE_LENGTH),
    attrs: z.record(z.string(), z.unknown()).optional(),
    content: z.array(TiptapNodeSchema).optional(),
    marks: z.array(TiptapMarkSchema).optional(),
    text: z.string().max(MAX_TEXT_NODE_LENGTH).optional(),
}));
// ---------------------------------------------------------------------------
// Document root
// ---------------------------------------------------------------------------
/**
 * A Tiptap document. Always rooted at a `doc` node; everything else lives
 * inside `content`. The top-level `superRefine` walks the tree once after
 * shape validation to enforce the global depth and node-count caps.
 *
 * Empty docs (no `content` array, or `content: []`) are valid — that's
 * what the editor produces when the user hasn't typed anything.
 */
export const TiptapDocumentSchema = z
    .object({
    type: z.literal("doc"),
    content: z.array(TiptapNodeSchema).optional(),
})
    .superRefine((doc, ctx) => {
    let nodeCount = 0;
    let maxDepthSeen = 0;
    const stack = (doc.content ?? []).map((n) => ({
        node: n,
        depth: 1,
    }));
    while (stack.length > 0) {
        const { node, depth } = stack.pop();
        nodeCount += 1;
        if (depth > maxDepthSeen)
            maxDepthSeen = depth;
        if (nodeCount > MAX_DOC_NODES) {
            ctx.addIssue({
                code: "custom",
                message: `Document exceeds maximum node count (${MAX_DOC_NODES})`,
            });
            return;
        }
        if (depth > MAX_DOC_DEPTH) {
            ctx.addIssue({
                code: "custom",
                message: `Document exceeds maximum nesting depth (${MAX_DOC_DEPTH})`,
            });
            return;
        }
        if (node.content) {
            for (const child of node.content) {
                stack.push({ node: child, depth: depth + 1 });
            }
        }
    }
});
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
/**
 * The empty-document literal a fresh Tiptap editor produces. Useful for
 * "draft has nothing in it" comparisons and as a default value.
 */
export const EMPTY_TIPTAP_DOC = { type: "doc", content: [] };
/**
 * True when the doc has no meaningful content — either no `content` array,
 * an empty array, or only empty paragraphs. Editors render a single empty
 * paragraph as the resting state, so a freshly-cleared doc looks like
 * `{ type: "doc", content: [{ type: "paragraph" }] }` and should still
 * count as empty.
 */
export function isEmptyTiptapDoc(doc) {
    if (!doc.content || doc.content.length === 0)
        return true;
    return doc.content.every((node) => {
        if (node.type !== "paragraph")
            return false;
        if (!node.content || node.content.length === 0)
            return true;
        return node.content.every((child) => child.type === "text" && (!child.text || child.text.length === 0));
    });
}
